import { isRecord } from '@/shared/utils/apiResponse';
import { formatTelefoneComCodigoPais } from '@/shared/utils/contato';

import type {
  CanalDaOrigem,
  CanalDeEntregaDaOrigem,
  EnderecoApresentado,
  EnderecoDaOrigem,
  OrigemApresentada,
  OrigemDoContato,
} from '../types/origemDoContato.types';

/**
 * "Correios" é o rótulo da entrega; a origem física usa o termo do design, "Carta AR · Envio físico".
 * O design não tem a variante RCS: o rótulo "RCS" (exibido como "RCS · telefone") é provisório até
 * a designer aprovar o texto, sem bloquear a entrega (resposta 5 da tarefa).
 */
const CANAIS_DA_ORIGEM: Record<
  CanalDaOrigem,
  { rotulo: string; canalDeEntrega: CanalDeEntregaDaOrigem }
> = {
  EMAIL: { rotulo: 'E-mail', canalDeEntrega: 'email' },
  WHATSAPP: { rotulo: 'WhatsApp', canalDeEntrega: 'whatsapp' },
  RCS: { rotulo: 'RCS', canalDeEntrega: 'rcs' },
  FISICO: { rotulo: 'Carta AR · Envio físico', canalDeEntrega: 'fisico' },
};

const CAMPOS_DO_ENDERECO = [
  'logradouro',
  'numero',
  'complemento',
  'bairro',
  'cidade',
  'uf',
  'cep',
] as const satisfies ReadonlyArray<keyof EnderecoDaOrigem>;

export const textoPreenchido = (valor: unknown): valor is string =>
  typeof valor === 'string' && valor.trim().length > 0;

/** `undefined` marca campo fora do contrato: chave ausente ou valor que não é texto nem `null`. */
function lerCampoDoEndereco(
  endereco: Record<string, unknown>,
  campo: keyof EnderecoDaOrigem,
): string | null | undefined {
  if (!Object.hasOwn(endereco, campo)) return undefined;
  const valor = endereco[campo];
  if (valor === null || typeof valor === 'string') return valor;
  return undefined;
}

function lerEnderecoDaOrigem(valor: unknown): EnderecoDaOrigem | null {
  if (!isRecord(valor)) return null;
  const endereco: EnderecoDaOrigem = {
    logradouro: null,
    numero: null,
    complemento: null,
    bairro: null,
    cidade: null,
    uf: null,
    cep: null,
  };
  for (const campo of CAMPOS_DO_ENDERECO) {
    const texto = lerCampoDoEndereco(valor, campo);
    if (texto === undefined) return null;
    endereco[campo] = texto;
  }
  // Endereço sem nenhum texto não identifica o contato: mostrá-lo como origem comprovada seria
  // apresentar um registro vazio como prova de quem agiu.
  return CAMPOS_DO_ENDERECO.some((campo) => textoPreenchido(endereco[campo])) ? endereco : null;
}

/**
 * Valida a origem recebida do core e a reconstrói só com os campos do contrato: nada além do
 * contato (captura, remetente bruto, BSUID, id do provedor) chega ao estado da tela, mesmo que o
 * core o mande por engano. Devolve `null` para origem fora do contrato; quem chama decide se isso
 * é erro, porque `null` aqui não significa "origem não identificada".
 */
export function lerOrigemDoContato(valor: unknown): OrigemDoContato | null {
  if (!isRecord(valor)) return null;
  switch (valor.canal) {
    case 'EMAIL':
      return textoPreenchido(valor.email) ? { canal: 'EMAIL', email: valor.email } : null;
    case 'WHATSAPP':
    case 'RCS':
      return textoPreenchido(valor.telefone)
        ? { canal: valor.canal, telefone: valor.telefone }
        : null;
    case 'FISICO': {
      const endereco = lerEnderecoDaOrigem(valor.endereco);
      return endereco ? { canal: 'FISICO', endereco } : null;
    }
    default:
      return null;
  }
}

function formatarCep(cep: string): string {
  const digitos = cep.replace(/\D/g, '');
  return digitos.length === 8 ? `${digitos.slice(0, 5)}-${digitos.slice(5)}` : cep.trim();
}

function cidadeComUf(cidade: string | null, uf: string | null): string | null {
  const cidadeLimpa = cidade?.trim();
  const ufLimpa = uf?.trim();
  if (cidadeLimpa && ufLimpa) return `${cidadeLimpa}/${ufLimpa}`;
  return cidadeLimpa || ufLimpa || null;
}

/**
 * Endereço integral no formato do design ("Rua das Palmeiras, 240, sala 12, Centro, São Paulo/SP,
 * CEP 01010-000"). Não reaproveita `formatEndereco`: o formato `padrao` dele descarta número,
 * complemento e bairro, e o `certificado` separa o bairro com travessão.
 */
function formatarEnderecoDaOrigem(endereco: EnderecoDaOrigem): EnderecoApresentado {
  const semCep = [
    endereco.logradouro,
    endereco.numero,
    endereco.complemento,
    endereco.bairro,
    cidadeComUf(endereco.cidade, endereco.uf),
  ]
    .map((parte) => parte?.trim())
    .filter((parte): parte is string => Boolean(parte))
    .join(', ');
  const cep = endereco.cep?.trim() ? `CEP ${formatarCep(endereco.cep)}` : null;
  return { semCep, cep, completo: [semCep, cep].filter(Boolean).join(', ') };
}

function contatoDaOrigem(origem: OrigemDoContato): string {
  switch (origem.canal) {
    case 'EMAIL':
      return origem.email;
    case 'WHATSAPP':
    case 'RCS':
      return formatTelefoneComCodigoPais(origem.telefone);
    case 'FISICO':
      return formatarEnderecoDaOrigem(origem.endereco).completo;
  }
}

/**
 * Texto de canal e contato de UMA origem. Cada chamada parte só da origem recebida (da conversão
 * ou de uma resposta): não há fallback para cadastro, entrega ou outra atribuição.
 */
export function apresentarOrigemDoContato(origem: OrigemDoContato): OrigemApresentada {
  const canal = CANAIS_DA_ORIGEM[origem.canal];
  return {
    canal: origem.canal,
    rotuloDoCanal: canal.rotulo,
    contato: contatoDaOrigem(origem),
    endereco: origem.canal === 'FISICO' ? formatarEnderecoDaOrigem(origem.endereco) : null,
    canalDeEntrega: canal.canalDeEntrega,
  };
}
